import { API_BASE } from "config/api";

const API_BASE_URL = API_BASE;

/**
 * Fetches CSRF token from Sanctum endpoint
 * @returns {Promise<boolean>} True if successful
 */
const getCsrfToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/sanctum/csrf-cookie`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });
    return response.ok;
  } catch (error) {
    console.error("CSRF Token Error:", error);
    return false;
  }
};

/**
 * Handles API responses and errors
 * @param {Response} response
 * @returns {Promise<any>}
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: `HTTP error! status: ${response.status}`,
      status: response.status,
    }));
    throw errorData;
  }
  return response.json();
};

export const apiService = {
  /**
   * Make a GET request
   * @param {string} endpoint
   * @param {Object} params
   * @returns {Promise<any>}
   */
  get: async (endpoint, params = {}) => {
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    return handleResponse(response);
  },

  /**
   * Make a POST request
   * @param {string} endpoint
   * @param {Object} data
   * @returns {Promise<any>}
   */
  post: async (endpoint, data) => {
    await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(data),
    });

    return handleResponse(response);
  },

  /**
   * Make a PUT request
   * @param {string} endpoint
   * @param {Object} data
   * @returns {Promise<any>}
   */
  put: async (endpoint, data) => {
    await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(data),
    });

    return handleResponse(response);
  },

  /**
   * Make a PATCH request
   * @param {string} endpoint
   * @param {Object} data
   * @returns {Promise<any>}
   */
  patch: async (endpoint, data) => {
    await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(data),
    });

    return handleResponse(response);
  },

  /**
   * Make a DELETE request
   * @param {string} endpoint
   * @returns {Promise<any>}
   */
  delete: async (endpoint) => {
    await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    return handleResponse(response);
  },
};

// Specific API endpoints
export const churchApi = {
  register: (data) => apiService.post("/churches", data),
  getAll: () => apiService.get("/churches"),
  getById: (id) => apiService.get(`/churches/${id}`),
  update: (id, data) => apiService.put(`/churches/${id}`, data),
  delete: (id) => apiService.delete(`/churches/${id}`),
};

export const authApi = {
  login: (credentials) => apiService.post("/login", credentials),
  logout: () => apiService.post("/logout"),
  register: (userData) => apiService.post("/register", userData),
  getUser: () => apiService.get("/user"),
};

// Additional API modules can be added here
// export const otherApi = { ... };
